import { z } from 'zod';
import { advisorRoleSchema, leadReifegradSchema, leadSideSchema, leadStatusSchema } from '../roles';
import { phoneSchema } from './lead';
import { displayNameSchema } from './directory';
/**
 * CRM (backoffice) contracts — advisor-facing endpoints under crm.finavest.ch.
 * Shared so the CRM frontend validates with the exact schemas the backend
 * enforces. All routes are session + RBAC protected (see backend crm module).
 */
/** Known funnel sources. `Lead.source` stays a free string in the DB; this is
 *  the canonical set the UI offers and analytics groups by. */
export const LEAD_SOURCES = [
    'calculator_hero',
    'seo',
    'direct',
    'referral',
    'mlg',
    'manual',
    // Immobilien-Bewertung funnel (map + valuation tool), per tenant. `Lead.source`
    // stays a free string in the DB, so this is purely additive — no migration.
    'mlg_valuation',
    'finavest_valuation',
    // Vermarktung A-4 (ontwerp §2.8): de exposé-mailgate. De KOPER-kant van het
    // landgoed en daarmee de eerste bron die geen Bewertungs-funnel is: iemand
    // vraagt op een publieke listing de Verkaufsdokumentation aan. De naam volgt
    // de canon-conventie `<tenant>_<funnel>` van de twee waarden hierboven, en
    // dat is hier meer dan netheid — `lead-retention.service.ts:176` filtert de
    // rohanfrage-purge op `source IN (VALUATION_SOURCES)`, dus een bron die
    // bewust BUITEN die lijst valt houdt de warme koperslead uit de
    // 14-dagen-quarantaine zonder dat er een tweede purge-tak bij hoeft.
    // Alleen `mlg`: fase A bedient uitsluitend MLG Immobilien AG (ontwerp §Tenancy).
    'mlg_expose_anfrage',
    // Vor-Ort-Bewertung («Immobilie verkaufen», contract docs/VOR-ORT-FORMULIER-CONTRACT.md
    // §3): a seller who asks for a visit. Same `<tenant>_<funnel>` canon, and — like
    // the exposé source — deliberately OUTSIDE `VALUATION_SOURCES`, so the rohanfrage
    // purge never touches it (the lead is visible from the start, contract §4 (b)).
    'mlg_vor_ort',
    // Order E1 (17-10): de Objekt-Anfrage (Besichtigung/Unterlagen) op een
    // Kauf-Objektseite — de KOPER-kant, zelfde canon en zelfde reden als de
    // exposé-bron: BUITEN `VALUATION_SOURCES`, dus nooit in de rohanfrage-purge.
    'mlg_objekt_anfrage',
];
export const knownLeadSourceSchema = z.enum(LEAD_SOURCES);
const nameSchema = z.string().trim().min(1).max(100);
const emailSchema = z.string().trim().toLowerCase().email().max(254);
const localeSchema = z.enum(['de', 'fr', 'it', 'en']);
const sourceSchema = z.string().trim().min(1).max(120);
const noteBodySchema = z.string().trim().min(1).max(5000);
// ── Leads ────────────────────────────────────────────────────────────────────
/** Manual lead creation by an advisor (POST /leads). */
export const createLeadSchema = z.object({
    firstName: nameSchema,
    lastName: nameSchema,
    email: emailSchema,
    phone: phoneSchema.optional(),
    locale: localeSchema.default('de'),
    source: sourceSchema.default('manual'),
    /** Kauf-/Verkauf-Funnel classification (WS-3a); preset when created from a board. */
    side: leadSideSchema.default('unknown'),
    assignedUserId: z.string().uuid().optional(),
    marketingConsent: z.boolean().default(false),
    /** Optional first note, stored alongside the lead. */
    note: noteBodySchema.optional(),
});
/** Partial lead update (PATCH /leads/:id) — status, owner and contact fields. */
export const updateLeadSchema = z
    .object({
    firstName: nameSchema,
    lastName: nameSchema,
    email: emailSchema,
    phone: phoneSchema.nullable(),
    locale: localeSchema,
    status: leadStatusSchema,
    side: leadSideSchema,
    source: sourceSchema,
    assignedUserId: z.string().uuid().nullable(),
    marketingConsent: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
/** B7: the two answers of the «Beratung» filter on the Kontakte list. */
export const LEAD_BERATUNG_FILTER = ['angefragt', 'keine'];
/** Lead list query (GET /leads). */
export const leadListQuerySchema = z.object({
    /** Case-insensitive search over first/last name and email. */
    q: z.string().trim().max(200).optional(),
    status: leadStatusSchema.optional(),
    /** Kauf-/Verkauf-Funnel board filter (WS-3a). */
    side: leadSideSchema.optional(),
    source: sourceSchema.optional(),
    /** Filter by owner; the literal 'unassigned' selects leads without an owner. */
    assignedUserId: z.union([z.string().uuid(), z.literal('unassigned')]).optional(),
    /**
     * Reifegrad filter (owner 26-8). ABSENT = the work view: everything except
     * `rohanfrage` (and never a test lead) — the quarantine is the default, not
     * an opt-in. Set explicitly to look at one rung.
     */
    reifegrad: leadReifegradSchema.optional(),
    /**
     * B7 (owner 29-9): «nur Bewertung» ↔ «auch telefonische Beratung». `angefragt`
     * = the lead asked for a call back at least once (`leads.beratungAngefragtAm`
     * set), `keine` = never. Absent = both. A closed list: anything else is a 400.
     */
    beratung: z.enum(LEAD_BERATUNG_FILTER).optional(),
    /** createdAt range (inclusive lower / exclusive upper bound). */
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    sort: z
        .enum(['createdAt', 'updatedAt', 'lastName', 'status', 'beratungAngefragtAm'])
        .default('createdAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
/**
 * Papierkorb query (GET /leads/trash) — the list query with the sort keys of
 * BEFORE B7. The Papierkorb always orders by `deletedAt` (newest deletion
 * first) and ignores `sort`, as it always did for the four old keys; the B7 key
 * `beratungAngefragtAm` is refused with a 400 instead of being ignored in
 * silence (nazorg RVB7 nit-2). The `beratung` filter IS applied there, like the
 * status/Quelle/search filters (`LeadsService.listTrash`).
 */
export const leadTrashQuerySchema = leadListQuerySchema.extend({
    sort: z.enum(['createdAt', 'updatedAt', 'lastName', 'status']).default('createdAt'),
});
// ── Leads — the wire shape of GET /leads (B7) ────────────────────────────────
/**
 * WHY THE ANSWER OF `GET /leads` IS A SHARED SCHEMA NOW (B7).
 *
 * The CRM typed this answer by hand (`LeadListItem` in `apps/crm/src/api/types.ts`),
 * and `apiFetch<T>` is an UNCHECKED generic — a cast, nothing more. A field the
 * server renamed or dropped was therefore invisible to every gate (the same class
 * `leadEingangAnzahlSchema` below documents). Since B7 the CRM type is DERIVED from
 * this schema, and the backend contract test parses the real answer with it —
 * `.strict()`, so a field that appears on one side only is red on the other.
 *
 * Dates travel as ISO strings (JSON of a `Date`).
 */
const isoZeitSchema = z.string().datetime();
/** The newest Beratungsanfrage of a lead, as the Kontakte list shows it. */
export const leadBeratungLetzteSchema = z
    .object({
    channel: z.string(),
    /** Free text ≤ 30 on the intake (`vormittags`, `egal`, …) — shown as sent. */
    preferredTime: z.string(),
    /** A `BERATUNG_TOPICS` key, or null (the Rechner/Vergleich forms send none). */
    topic: z.string().nullable(),
    language: z.string(),
    /** `rechner` | `vergleich` | `bewertung`, or null (classic Rechner step 3). */
    triggerSource: z.string().nullable(),
})
    .strict();
/**
 * `beratung` on a list item: null = the lead never asked for a call back.
 * `angefragtAm` is the lead's own column (`beratungAngefragtAm`, the newest
 * request); `letzte` is that request's words. `letzte` is nullable only as a
 * defensive reading — the ingest writes row and column in one transaction and
 * the migration backfills the column FROM the rows, so both exist together.
 */
export const leadListBeratungSchema = z
    .object({
    angefragtAm: isoZeitSchema,
    letzte: leadBeratungLetzteSchema.nullable(),
})
    .strict();
/**
 * Vor-Ort (A, master 30-9) — `vorOrt` on a list item: what the column «Vor-Ort»
 * shows, «‹Wunschzeitraum› · ‹PLZ›». null = the lead has no Vor-Ort-Anfrage
 * (every lead that did not come from the form). The technical word travels;
 * the CRM translates it (`VOR_ORT_ZEITRAUM_LABELS`) and shows a word it does
 * not know yet as it is. There is deliberately no filter next to it: the
 * Quelle «Vor-Ort-Bewertung» (`source=mlg_vor_ort`) already selects these leads.
 */
export const leadListVorOrtSchema = z
    .object({
    zeitraum: z.string(),
    plz: z.string(),
})
    .strict();
/**
 * Order E1 (17-10) — `objektAnfrage` on a list item: the ANLIEGEN of the lead's
 * Objekt-Anfrage (`besichtigung` | `unterlagen`), shown as a chip next to the
 * Quelle «Objekt-Anfrage». null = the lead has no Objekt-Anfrage. The technical
 * word travels; the CRM translates it (`OBJEKT_ANFRAGE_ANLIEGEN_LABELS`) and shows
 * a word it does not know yet as it is. No filter: the Quelle selects these leads.
 */
export const leadListObjektAnfrageSchema = z.object({ anliegen: z.string() }).strict();
/** One row of `GET /leads` — `LEAD_SELECT` + the resolved owner + `beratung` + `vorOrt` + `objektAnfrage`. */
export const leadListItemSchema = z
    .object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    locale: z.string(),
    status: leadStatusSchema,
    /** Kauf-/Verkauf-Funnel classification (WS-3a). */
    side: leadSideSchema,
    source: z.string().nullable(),
    utm: z.record(z.string(), z.string()).nullable(),
    marketingConsent: z.boolean(),
    customerAccountId: z.string().nullable(),
    assignedUserId: z.string().nullable(),
    assignedUser: z.object({ id: z.string(), email: z.string() }).strict().nullable(),
    /** Reifegrad ladder (owner 26-8) + its proofs; see LeadReifegrad in roles. */
    reifegrad: leadReifegradSchema,
    reifegradSeit: isoZeitSchema,
    phoneVerifiedAt: isoZeitSchema.nullable(),
    emailVerifiedAt: isoZeitSchema.nullable(),
    istTest: z.boolean(),
    createdAt: isoZeitSchema,
    updatedAt: isoZeitSchema,
    beratung: leadListBeratungSchema.nullable(),
    vorOrt: leadListVorOrtSchema.nullable(),
    objektAnfrage: leadListObjektAnfrageSchema.nullable(),
})
    .strict();
/** The whole answer of `GET /leads`. */
export const leadListAntwortSchema = z
    .object({
    items: z.array(leadListItemSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
})
    .strict();
/**
 * One Beratungsanfrage on the lead page (`GET /leads/:id` → `beratungsanfragen`,
 * newest first). `taskId` = the Rückruf task this request created (null for a
 * test lead, which gets no task, and for rows the migration backfilled);
 * `consultationId` is reserved for a later link to the slot booking and null today.
 */
export const leadBeratungsanfrageSchema = z
    .object({
    id: z.string(),
    channel: z.string(),
    preferredTime: z.string(),
    topic: z.string().nullable(),
    language: z.string(),
    triggerSource: z.string().nullable(),
    quelle: z.string(),
    taskId: z.string().nullable(),
    consultationId: z.string().nullable(),
    createdAt: isoZeitSchema,
})
    .strict();
/**
 * «Eingang» (owner 26-8): the quarantine list of `rohanfrage` and test leads.
 * Deliberately its OWN small query — no status/side/owner filters, because a
 * Rohanfrage has none of those yet, and no free-text search: the list shows
 * e-mail DOMAINS only, so there is nothing PII-shaped to search.
 */
export const leadEingangQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
/**
 * Het antwoord van `GET /leads/eingang/anzahl` — het getal achter de
 * menu-badge (review #490 NEW-1).
 *
 * WAAROM DIT EEN GEDEELD TYPE IS EN GEEN AFSPRAAK. De badge las `r.anzahl`,
 * de server stuurde `{ total }`: `undefined`, dus de teller bleef op nul en er
 * verscheen nóóit een badge. Geen enkele poort zag het, want `apiFetch<T>` is
 * een ONGECONTROLEERDE generic — puur een cast, dus `typecheck` had niets om
 * tegen te botsen. Dat is erger dan een cosmetisch bedrag: de Eingang is de
 * enige plek waar een Rohanfrage zichtbaar is vóór hij na 14 dagen HARD wordt
 * verwijderd, en het commentaar bij de badge belooft precies dat. Nu botst een
 * hernoeming aan beide kanten.
 */
export const leadEingangAnzahlSchema = z.object({ total: z.number().int().nonnegative() }).strict();
// ── Notes ────────────────────────────────────────────────────────────────────
export const createNoteSchema = z.object({ body: noteBodySchema });
// ── Email marketing — templates ──────────────────────────────────────────────
// De Vorlagen-contracten (ex-WS-3a `emailTemplate*`) leven sinds Kommunikation
// PR-1 in `schemas/kommunikation.ts` (`mailVorlage*`): één register voor ALLE
// uitgaande sjabloonmail, holding- en tenant-laag.
// ── Email marketing — newsletter (WS-3b) ─────────────────────────────────────
/** Newsletter lifecycle. Mirrors the Prisma `NewsletterStatus` enum. */
export const newsletterStatusSchema = z.enum(['draft', 'sending', 'sent', 'failed']);
/**
 * Compose a newsletter (POST /kommunikation/newsletters — Kommunikation PR-3). Sent to every lead
 * with a standing marketing consent (single-opt-in + unsubscribe link). The body
 * may carry `{{…}}` placeholders resolved per recipient at send time.
 */
export const newsletterCreateSchema = z.object({
    subject: z.string().trim().min(1).max(300),
    bodyHtml: z.string().trim().min(1).max(200_000),
});
/**
 * Correct a campaign before it goes out (PATCH /kommunikation/newsletters/:id).
 * Only a `draft`/`failed` campaign is editable — the backend 409s otherwise.
 */
export const newsletterUpdateSchema = z
    .object({
    subject: z.string().trim().min(1).max(300),
    bodyHtml: z.string().trim().min(1).max(200_000),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
// ── Tasks (basis reminders) ──────────────────────────────────────────────────
export const taskStatusSchema = z.enum(['open', 'in_progress', 'done', 'cancelled']);
/**
 * Priority (W5-5). Three values on purpose — a five-point scale invites
 * "medium-high" and then nobody sorts by it. `normal` is the default, so every
 * task that existed before W5-5 keeps meaning exactly what it meant.
 */
export const taskPrioritySchema = z.enum(['low', 'normal', 'high']);
/**
 * Approval state (W5-5) — a SEPARATE axis from `status`, because "done" and
 * "approved" are different claims made by different people. `not_required` is
 * the default and what every pre-W5-5 task is.
 */
export const taskApprovalSchema = z.enum(['not_required', 'pending', 'approved', 'rejected']);
/**
 * One checklist sub-item. The `id` is client-generated (a uuid) so a re-order
 * or a partial edit does not depend on array position — the list is replaced
 * wholesale on every write, and without stable ids two people editing at once
 * would silently tick each other's boxes.
 */
export const taskChecklistItemSchema = z.object({
    id: z.string().uuid(),
    text: z.string().trim().min(1).max(200),
    done: z.boolean(),
});
/**
 * The whole checklist. THIS is what makes the JSONB column honest: the shape is
 * validated on every write, and both bounds below are the guard that a JSON
 * column otherwise lacks — a cap on the number of items, and (via the item
 * schema) a cap on each one's length. Duplicate ids are refused, because the
 * whole point of the id is that it identifies exactly one item.
 */
export const taskChecklistSchema = z
    .array(taskChecklistItemSchema)
    .max(50)
    .refine((items) => new Set(items.map((i) => i.id)).size === items.length, {
    message: 'duplicate_checklist_item_id',
});
export const createTaskSchema = z.object({
    title: z.string().trim().min(1).max(200),
    leadId: z.string().uuid().optional(),
    financingRequestId: z.string().uuid().optional(),
    /** Defaults to the creating advisor when omitted. */
    assignedUserId: z.string().uuid().optional(),
    dueAt: z.string().datetime().optional(),
    /** W5-5; omitted = `normal`, the server default. */
    priority: taskPrioritySchema.optional(),
    /** W5-5; omitted = empty. */
    checklist: taskChecklistSchema.optional(),
});
export const updateTaskSchema = z
    .object({
    title: z.string().trim().min(1).max(200),
    status: taskStatusSchema,
    assignedUserId: z.string().uuid().nullable(),
    dueAt: z.string().datetime().nullable(),
    /** W5-5. */
    priority: taskPrioritySchema,
    /** W5-5. REPLACES the whole list — see taskChecklistSchema. */
    checklist: taskChecklistSchema,
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
/**
 * Approval transitions (W5-5), as an explicit ACTION rather than a settable
 * field. Deliberately not part of `updateTaskSchema`: an approval is an event
 * with an actor and a moment, and letting it ride along in a partial update
 * would make "who approved this and when" a side effect of editing a title.
 *
 * `request` is the only transition anybody may make on their own task;
 * `approve`/`reject` answer a pending request. `clear` withdraws it back to
 * `not_required` — the escape hatch for a request raised by mistake.
 */
export const taskApprovalActionSchema = z.object({
    action: z.enum(['request', 'approve', 'reject', 'clear']),
});
/** A comment on a task (W5-5). */
export const createTaskCommentSchema = z.object({
    body: z.string().trim().min(1).max(4000),
});
export const taskListQuerySchema = z.object({
    status: taskStatusSchema.optional(),
    /** 'open_any' = open + in_progress (the default working set). */
    scope: z.enum(['open_any', 'all']).default('open_any'),
    assignedUserId: z.string().uuid().optional(),
    /** W5-5. */
    priority: taskPrioritySchema.optional(),
    /** W5-5; 'pending' is the reviewer's queue. */
    approval: taskApprovalSchema.optional(),
    /**
     * W5-5 Papierkorb. `true` lists ONLY soft-deleted tasks; absent/false lists
     * only live ones. There is deliberately no "both" — a list that mixes live
     * and trashed rows is a list nobody can act on.
     */
    trashed: z.preprocess((v) => v === 'true' || v === true, z.boolean()).optional(),
    leadId: z.string().uuid().optional(),
    dueBefore: z.coerce.date().optional(),
    /** Lower bound on dueAt (inclusive), symmetric with `dueBefore`. Lets the
     *  agenda cap the overdue window so an ancient backlog can't crowd out
     *  near-term tasks behind the page cap. */
    dueAfter: z.coerce.date().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
// ── User management (admin) ──────────────────────────────────────────────────
/**
 * THE MEMBERSHIP VOCABULARY — which tenants a STAFF membership may name.
 *
 * Split off from `TENANTS` (schemas/valuation.ts) by S-5. Until then the two
 * were one list, and the review of #388 measured what that cost:
 * `updateUserSchema.safeParse({ tenantSlugs: ['holding'] })` → `success: false`,
 * i.e. the holding realm the S-5 migration creates had no route by which anyone
 * could be given access to it. `apps/control/src/lib/membership-slugs.ts`
 * documented the same gap for `gu`, which is the one this change closes; the
 * wizard's block stays, now only for tenants that are genuinely new.
 *
 * WHY A SECOND LIST AND NOT A WIDER `TENANTS`. `TENANTS` is the PUBLIC funnel's
 * brand discriminator: it validates `POST /ingest/valuation` and is consumed by
 * a binary expression in `ingest.service.ts` that maps everything-not-finavest
 * to `mlg_valuation`. Widening it there was measured to turn a 400 into a 202
 * with persisted rows under a wrong source. A membership is a different fact
 * from a funnel brand, so it gets its own list — the first of the two options
 * `membership-slugs.ts` names, and the one that keeps a CLOSED enum on the
 * identity write path.
 *
 * STILL NOT EXHAUSTIVE, AND STILL CLOSED. Each value is admitted by its own
 * decision, never as a side effect of the change that needed it:
 *  - `holding` (S-5) — the DRIP admin's home realm, which the migration creates.
 *  - `gu` (BAU test phase) — MLG Generalunternehmung. It has existed since the
 *    basalt migration, but its memberships only ever came from `seed.ts`, so the
 *    Benutzerverwaltung route into it had never actually been driven. The test
 *    phase needs Justin and Levin given a membership + role there through the
 *    screen, which is exactly the decision this list was split off to make
 *    separately: adding it here widens the IDENTITY vocabulary and nothing else.
 *  - `agentur` (Agentur-Strang P1.1, docs/pricing-commerce/10 §4) — the
 *    in-house marketing agency, MLG Marketing, created by migration
 *    `20260907120000_agentur_tenant`. Staff-only: it has no customer surface
 *    and no funnel, so it belongs here and nowhere else.
 * Everything a future C6-8 run creates is still outside, and
 * `TenantService.resolveSlugs` remains the fail-closed authority behind this
 * schema — an unknown slug is a 400 there even if it passes here.
 */
export const MEMBERSHIP_TENANTS = ['mlg', 'finavest', 'holding', 'gu', 'agentur'];
export const membershipTenantSchema = z.enum(MEMBERSHIP_TENANTS);
/**
 * Tenant assignment for a staff member (M4) — by slug. At least one: a user
 * without any membership would be locked out of every tenant. Bounded length as
 * a guard.
 */
export const tenantSlugsSchema = z.array(membershipTenantSchema).min(1).max(10);
/**
 * ONYX role vocabulary for the Verwaltung department (F2c). Mirrors the
 * backend's Prisma enum by hand — this package also feeds the browser bundle,
 * so it cannot import @prisma/client (same precedent as advisorRoleSchema).
 * null = the Verwaltung department does not exist for this staff member.
 */
export const verwaltungRolleSchema = z.enum(['ADMIN', 'BEWIRTSCHAFTUNG', 'NUR_LESEN']);
/** PATCH /auth/advisor/users/:id — role/active changes revoke live sessions. */
export const updateUserSchema = z
    .object({
    role: advisorRoleSchema,
    isActive: z.boolean(),
    /**
     * Display name (W5-4). `null` CLEARS it, and that is a real choice rather
     * than a validation accident: an empty column falls back to the derived
     * name, so clearing is how an admin says "we do not actually know this"
     * instead of leaving a wrong name standing as if it were typed.
     */
    displayName: displayNameSchema.nullable(),
    /** Admin-set new password (e.g. onboarding/reset); revokes the user's sessions. */
    password: z.string().min(12).max(200),
    /** Replace the staff member's tenant memberships (`MEMBERSHIP_TENANTS`). */
    tenantSlugs: tenantSlugsSchema,
    /** Verwaltung (ONYX) access: role or null to revoke (F2c). */
    verwaltungRolle: verwaltungRolleSchema.nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
// ── Analytics (interne lead-statistieken) ────────────────────────────────────
export const analyticsQuerySchema = z.object({
    /** Defaults to the last 30 days when omitted. */
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
});
